<template>
  <div
    ref="drawerRef"
    :class="[
      'fixed z-60 h-100dvh md:max-h-97dvh rounded-lg md:mr-4 md:mt-4 md:w-[540px] overflow-x-hidden bg-white dark:bg-gray-800 drawer grid'
    ]"
  >
    <div
      v-if="includeHeader"
      class="absolute bg-white text-white w-full z-10 text-xs"
    >
      <slot name="drawerHeader"> </slot>
    </div>
    <div
      :class="[
        'p-4 relative rounded-t-lg bg-white z-20',
        { 'mt-9 header-box-shadow': includeHeader }
      ]"
    >
      <slot name="title">
        <div class="flex gap-3">
          <div v-if="backArrow" class="cursor-pointer" @click="closeDrawer">
            <CaretLeft class="h-6" />
          </div>
          <h5
            class="mb-4 inline-flex items-center text-base font-semibold text-gray-900"
          >
            {{ title }}
          </h5>
        </div>
      </slot>
      <button
        v-if="!backArrow"
        type="button"
        aria-controls="drawer-example"
        class="absolute right-2.5 top-2.5 inline-flex h-8 w-8 items-center justify-center rounded-lg bg-transparent text-sm text-gray-400 hover:bg-gray-200 hover:text-gray-900 dark:hover:bg-gray-600 dark:hover:text-white cursor-pointer"
        @click="closeDrawer"
      >
        <CloseIcon class="w-4" />
        <span class="sr-only">Close menu</span>
      </button>
    </div>
    <div class="overflow-y-auto">
      <slot></slot>
    </div>
    <div
      v-if="!noFooter"
      class="sticky bottom-0 p-4 bg-white flex justify-center w-full"
    >
      <slot name="footer"></slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { DrawerInterface, DrawerOptions } from "flowbite";
import { Drawer } from "flowbite";
import { onMounted, ref } from "vue";
import { generateUID } from "@/lib/helpers";

import CaretLeft from "@/images/CaretLeft.svg?component";
import CloseIcon from "@/images/x.svg?component";

const emit = defineEmits(["hide-drawer", "show-drawer"]);
const props = withDefaults(
  defineProps<{
    title?: string;
    includeHeader?: boolean;
    backArrow?: boolean;
    noFooter?: boolean;
    backdrop?: boolean;
  }>(),
  {
    title: "",
    backdrop: true,
    backArrow: false,
    includeHeader: false,
    noFooter: false
  }
);
// set the drawer menu element
const drawerRef = ref(null);
const drawer = ref<DrawerInterface | null>(null);

onMounted(() => {
  // options with default values
  const options: DrawerOptions = {
    placement: "right",
    backdrop: props.backdrop,
    bodyScrolling: false,
    edge: false,
    edgeOffset: "",
    backdropClasses:
      "bg-gray-900/50 dark:bg-gray-900/80 fixed inset-0 z-50 " + generateUID(),
    onHide: () => {
      setTimeout(() => {
        emit("hide-drawer");
      }, 500);
    },
    onShow: () => {
      emit("show-drawer");
    }
  };

  /*
   * $targetEl (required)
   * options (optional)
   * instanceOptions (optional)
   */
  drawer.value = new Drawer(drawerRef.value, options, {
    id: generateUID().toString()
  });

  // show the drawer
  drawer.value?.show();
});

const closeDrawer = () => drawer.value?.hide();
</script>

<style lang="scss">
.drawer.grid {
  grid-template-rows: 1fr 14fr 1fr;
}
.header-box-shadow {
  box-shadow: 0px -5px 11px -3px rgba(0, 0, 0, 0.15);
}
</style>
